import { useTranslation } from "@sg/i18n";
import { BaseDrawer, Checkbox, notify } from "@sg/ui";
import { capitalize } from "lodash";
import { Fragment, useMemo } from "react";
import { useAddRoleV2, useRemoveRoleV2, useUser } from "~/hooks/query";
import { RoleV2 } from "~/types";

interface UserRoleDrawerProps {
  open?: boolean;
  onClose: () => void;
  id?: string;
  orgId?: string;
}

const EXCLUDED_ROLES = [RoleV2.COMPLIANCE, RoleV2.SUPER_ADMIN];

export function UserRoleDrawer({ open, onClose, id, orgId }: UserRoleDrawerProps) {
  const { t } = useTranslation();
  const { data, isLoading: isLoadingUser, dataUpdatedAt } = useUser(id);
  const { mutate: addRole, isLoading: isAdding } = useAddRoleV2();
  const { mutate: removeRole, isLoading: isRemoving } = useRemoveRoleV2();

  const loading = isLoadingUser;
  const mutating = isAdding || isRemoving;

  function onCloseDrawer() {
    onClose();
  }

  const { roleMap } = useMemo(() => {
    const userRoles = data?.roles || [];
    const roleMap: Record<string, boolean> = {};

    userRoles.forEach((r) => {
      roleMap[r.role] = true;
    });

    return {
      roles: userRoles.map((u) => u.role),
      roleMap,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  return (
    <BaseDrawer
      hidePrimary
      title={t("role:assign_role")}
      subtitle={t("role:assign_role_prompt", { name: `${data?.first_name || ""} ${data?.last_name || ""}` })}
      open={open}
      onClose={onCloseDrawer}
      loading={loading}
      closeLabel={t("close")}
    >
      {Object.values(RoleV2).map((role) => {
        if (EXCLUDED_ROLES.includes(role)) return <Fragment key={role}></Fragment>;
        return (
          <Checkbox
            key={role}
            label={capitalize(role)}
            name="role"
            checked={!!roleMap[role]}
            disabled={mutating}
            onCheckedChange={async (v) => {
              if (!id || !orgId) {
                notify.alert(t("invalid_data"));
                return;
              }

              const payload = {
                org_id: orgId,
                role,
                user_id: id,
              };

              console.log(payload);
              if (v) {
                await addRole(payload);
              } else {
                await removeRole(payload);
              }
            }}
          />
        );
      })}
    </BaseDrawer>
  );
}
